import { useEffect, useState } from 'react'
import axios from '@services/axiosInterceptorInstance'
import {
  AppsFlyerKeys,
  AppsFlyerParams,
  EmptyGuid,
  FacebookEvents,
  NEXT_GET_ORDER_RELATED_PRODUCTS,
  NEXT_GET_WISHLIST,
  NEXT_REMOVE_WISHLIST,
  ProdutId,
} from '@components/utils/constants'
import { useUI } from '@components/ui/context'
import WishlistHeader from './WishlistHeader'
import WishlistSection from './WishlistSection'
import cartHandler from '@components/services/cart'
import Spinner from '@components/ui/Spinner'
import {
  INSUFFICIENT_STOCK,
  MOVED_TO_BAG,
  PROD_ADDED_SUCCESSFULLY,
} from '@components/utils/textVariables'
import { matchStrings } from '@framework/utils/parse-util'
import {
  eventIdForFB,
  getBrowserIdForFBConversion,
  getCategories,
  getColorAndSize,
  getCommonAppsFlyerValue,
  getCurrentPage,
  getDateOfBirthHash,
  getGenderHash,
  getIpAddressForFBConversion,
  isCartAssociated,
  openNewCartPage,
} from '@framework/utils/app-util'
import useSection from 'hooks/useSection'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { useGoogleAnalyticsEvent } from 'hooks/useGAEvent'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import { facebookEventHandler } from 'hooks/facebook-conversion'
import { getSHA256Hash } from '@framework/utils/cipher'
import LoggedOutSection from './LoggedOutSection'
import WishlistEmpty from './WishlistEmpty'

declare const window: any
declare const AF: any

interface IWishlistProps {
  previousPath: string
}

function Wishlist(props: IWishlistProps) {
  const { previousPath } = props
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const {
    user,
    basketId,
    setCartItems,
    openCart,
    setWishlist,
    cartItems,
    setAlert,
    isGuestUser,
  } = useUI()
  const { handleSection } = useSection()
  const trackGoogleAnalyticsEvent = useGoogleAnalyticsEvent()
  const trackMoEngageEvent = useMoEngageEvent()
  const isUserLoggedIn =
    !isGuestUser && user?.userId && user?.userId !== EmptyGuid

  const fetchItems = async () => {
    !isLoading && setIsLoading(true)
    try {
      const response: any = await axios.post(NEXT_GET_WISHLIST, {
        id: user.userId,
        flag: true,
      })
      setIsLoading(false)
      setData(response.data)
      setWishlist(response.data)
    } catch (error) {
      console.error(':: error', error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchItems()
  }, [])

  const getlineItemSizeWithoutSlug = (product: any) =>
    product?.variantProducts
      ?.filter((vp: any) => vp?.stockCode === product?.stockCode)[0]
      ?.variantAttributes.filter(
        (va: any) => va.fieldCode === 'clothing.size'
      )[0]?.fieldValue

  const handleAddToCart = async (product: any) => {
    let replacementEligibilityDays: Number | null = null
    let returnEligibilityDays: Number | null = null
    const returnEligeble = product?.attributes
      ?.find((x: any) => x.key == 'product.returnsandexchange')
      ?.value?.toLowerCase()
    const replacementEligeble = product?.attributes
      ?.find((x: any) => x.key == 'product.replacement')
      ?.value?.toLowerCase()

    const replacementEligibility =
      replacementEligeble === 'true' &&
      product?.attributes?.find(
        (x: any) => x.key == 'product.replacementeligibility'
      )?.value

    const replacementEligibilityMatch = JSON.stringify(
      replacementEligibility
    )?.match(/\d+/)

    if (replacementEligibilityMatch) {
      const number = parseInt(replacementEligibilityMatch[0], 10)
      replacementEligibilityDays = number
    }

    const returnEligibility =
      returnEligeble === 'true' &&
      product?.attributes?.find(
        (x: any) => x.key == 'product.returnandexchangeeligibility'
      )?.value

    const returnEligibilityMatch =
      JSON.stringify(returnEligibility)?.match(/\d+/)

    if (returnEligibilityMatch) {
      const number = parseInt(returnEligibilityMatch[0], 10)
      returnEligibilityDays = number
    }

    cartHandler()
      .addToCart(
        {
          basketId,
          productId: product.recordId,
          qty: 1,
          stockCode: product.stockCode,
          userId: user.userId,
          isAssociated: isCartAssociated(cartItems), //user.isAssociated,
          CustomInfo4: JSON.stringify({
            formatted: {
              title: 'Replacement Eligibility',
              data: {
                'Replacement Eligibility': replacementEligibilityDays || null,
              },
            },
          }),
          CustomInfo5: JSON.stringify({
            formatted: {
              title: 'Return Eligibility',
              data: {
                'Return Eligibility': returnEligibilityDays || null,
              },
            },
          }),
          CustomInfo4Formatted: replacementEligibilityDays?.toString() || '',
          CustomInfo5Formatted: returnEligibilityDays?.toString() || '',
        },
        'ADD',
        { product }
      )
      .then(async (response: any) => {
        if (
          response?.message &&
          !matchStrings(response?.message, PROD_ADDED_SUCCESSFULLY, true)
        ) {
          setAlert({ type: 'error', msg: INSUFFICIENT_STOCK })
        } else if (response?.id === EmptyGuid) {
          setAlert({ type: 'error', msg: INSUFFICIENT_STOCK })
        } else {
          setCartItems(response)
          handleRemoveFromWishlist(product)
          //           openCart();
          await openNewCartPage(openCart, 'item_added')
          await setAlert({ type: 'success', msg: MOVED_TO_BAG })
        }
      })
      .catch((err: any) => console.error(':: error', err))

    const eventId = eventIdForFB()
    trackGoogleAnalyticsEvent(
      GA_EVENT.ADD_TO_CART,
      {
        ecommerce: {
          items: [
            {
              item_name: product?.name,
              item_brand: product?.brand,
              item_category2: getCategories(product).category2,
              item_category: getCategories(product).category,
              item_variant: getColorAndSize(product),
              color: product?.attributes.find(
                (x: any) => x?.key === 'product.colours'
              )?.value,
              quantity: 1,
              item_id: product?.sku,
              item_size: getlineItemSizeWithoutSlug(product),
              price: product?.price?.raw?.withTax,
              item_var_id: product?.stockCode,
              item_list_name: getCategories(product).category,
              index: 1,
            },
          ],
          cart_quantity: 1,
          total_value: product?.price?.raw?.withTax,
          current_page: 'Wishlist',
          section_title: 'Wishlist',
          event_id: eventId,
          loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
        },
      },
      window
    )

    const mobileNumber = user?.mobile || user?.telephone
    let facebookeventData = {
      data: [
        {
          event_name: FacebookEvents.ADD_TO_CART,
          event_time: Math.floor(new Date().getTime() / 1000),
          event_id: eventId,
          action_source: 'website',
          user_data: {
            em: [user?.email ? await getSHA256Hash(user?.email) : ''],
            ph: mobileNumber ? await getSHA256Hash(mobileNumber) : null,
            fn: user?.firstName ? await getSHA256Hash(user?.firstName) : null,
            ln: user?.lastName ? await getSHA256Hash(user?.lastName) : null,
            client_user_agent: window.navigator.userAgent,
            fbp: getBrowserIdForFBConversion(),
            client_ip_address: (await getIpAddressForFBConversion()) || null,
            external_id: mobileNumber
              ? await getSHA256Hash(mobileNumber)
              : null,
            db: await getDateOfBirthHash(user),
            ge: await getGenderHash(user?.gender),
            ct: null,
            zp: null,
            country: await getSHA256Hash('in'),
          },
          custom_data: {
            currency: 'INR',
            num_items: 1,
            content_name: getCurrentPage(),
            value: product?.price?.raw?.withTax,
            content_category: getCategories(product).category,
            content_ids: [product?.stockCode],
            content_type: 'product',
            contents: [
              {
                id: product?.recordId,
                quantity: 1,
                item_price: product?.price?.raw?.withTax,
                delivery_category: 'home_delivery',
              },
            ],
          },
        },
      ],
    }

    facebookEventHandler(facebookeventData)

    if (typeof AF !== 'undefined') {
      const commonEventValue = getCommonAppsFlyerValue()
      AF('pba', 'event', {
        eventType: 'EVENT',
        eventName: AppsFlyerKeys.AddToCart,
        eventRevenueCurrency: 'INR',
        eventValue: {
          [AppsFlyerParams.AFEventParam3]: 'Wishlist',
          [AppsFlyerParams.AFEventParamRevenue]:
            product?.price?.raw?.withTax + '',
          [AppsFlyerParams.AFEventParamContent]: product?.name,
          [AppsFlyerParams.AFEventParamContentId]: product?.stockCode,
          [AppsFlyerParams.AFEventParamPrice]:
            product?.price?.raw?.withTax + '',
          [AppsFlyerParams.AFEventParamQuantity]: 1 + '',
          [AppsFlyerParams.AFEventParam5]:
            product?.classification?.mainCategoryName || '',
          [AppsFlyerParams.AFEventParam6]:
            product?.classification?.category || '',
          ...commonEventValue,
        },
      })
    }

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.ADD_TO_CART,
      {
        Product_size: getlineItemSizeWithoutSlug(product),
        URL: window?.location?.href,
        Cart_quantity: 1,
        Cart_total: product?.price?.raw?.withTax,
        Product_name: product?.name,
        Product_ID: product?.stockCode,
        Price: product?.price?.raw?.withTax,
        label: 'Add To Bag',
        action: 'Click',
        Quantity: 1,
        Image: product?.image,
        Product_category: product?.classification?.mainCategoryName || '',
        Product_category_2: product?.classification?.category || '',
        Product_color: product?.variantGroupCode,
      },
      window
    )
    const currentPage = getCurrentPage()
    if (currentPage) {
      if (typeof window !== 'undefined') {
        let color = ''
        if (product?.variantGroupCode) {
          color = product?.attributes?.find(
            (datum: any) => datum.key === 'product.colours'
          )?.value
        }
      }
    }
  }

  const handleRemoveFromWishlist = (product: any) => {
    const handleAsync = async () => {
      try {
        await axios.post(NEXT_REMOVE_WISHLIST, {
          id: user?.userId,
          productId: product?.recordId,
          flag: true,
        })
        fetchItems()
      } catch (error) {
        console.error(':: error', error)
      }
    }
    let productAvailability = 'Yes'
    if (product?.currentStock > 0) {
      productAvailability = 'Yes'
    } else {
      productAvailability = 'No'
    }

    recordGA4Event(window, GA_EVENT.REMOVE_ITEM, {
      product_name: product?.name,
      availability: productAvailability,
      product_id: product?.sku,
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })
    handleAsync()
  }

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      const { data: relatedProducts }: any = await axios.post(
        NEXT_GET_ORDER_RELATED_PRODUCTS,
        {
          id: ProdutId,
        }
      )
    }
    fetchRelatedProducts()
  }, [])

  const handleViewItem = (product: any) => {
    if (typeof window !== 'undefined') {
      let color = ''
      if (product?.variantGroupCode) {
        color = product.variantGroupCode.split('-')[1]
      } else if (product?.attributes?.length) {
        color = product?.attributes?.find(
          (datum: any) => datum.key === 'product.colours'
        )?.value
      }
      handleSection('')
    }
  }

  return (
    <>
      <WishlistHeader data={data} previousPath={previousPath} />
      <div className="bg-white">
        {/* Mobile menu */}
        {isUserLoggedIn ? (
          <main className="sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {isLoading ? (
                <Spinner />
              ) : (
                <>
                  {data?.length > 0 ? (
                    <WishlistSection
                      data={data}
                      isLoading={isLoading}
                      handleViewItem={handleViewItem}
                      handleAddToCart={handleAddToCart}
                      handleRemoveFromWishlist={handleRemoveFromWishlist}
                    />
                  ) : (
                    <WishlistEmpty />
                  )}
                </>
              )}
            </div>
            {/* blog info ui end */}
          </main>
        ) : (
          <div className="max-w-4xl mx-auto">
            <LoggedOutSection screen="wishlist" />
          </div>
        )}
      </div>
    </>
  )
}

export default Wishlist
